import { useTheme } from "@emotion/react";
import { PersonAdd } from "@mui/icons-material";
import {
    Alert,
    Avatar,
    Box,
    Button,
    Grid,
    TextField,
    Typography
} from "@mui/material";
import { useState, useEffect } from "react";
import { useMicrospecThemeLanguageContext } from "../Themes";
import { useMicrospecDynamicUIContext } from "../GlobalProvider";
import AuthWrapper from "./AuthWrapper";

const ProfileCompletion = ({
    identifier,
    countryCode,
    loading,
    error,
    onProfileComplete,
    onBackToIdentifier
}) => {
    const { localizationProvider } = useMicrospecThemeLanguageContext();
    const { cloudProvider } = useMicrospecDynamicUIContext();
    const theme = useTheme();

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [formErrors, setFormErrors] = useState({});

    // Clean up any existing reCAPTCHA when component mounts
    useEffect(() => {
        if (cloudProvider?.userProfile?.methods?.cleanupRecaptcha) {
            cloudProvider.userProfile.methods.cleanupRecaptcha();
        }
    }, [cloudProvider]);

    // Generate gradients from theme colors
    const primaryGradient = `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`;

    const handleSubmit = (event) => {
        event.preventDefault();

        // Validate form
        const errors = {};
        if (!firstName.trim()) {
            errors.firstName = localizationProvider.translateByType({
                type: "Internal",
                text: "firstNameRequired"
            }) || "الاسم الأول مطلوب";
        }
        if (!lastName.trim()) {
            errors.lastName = localizationProvider.translateByType({
                type: "Internal",
                text: "lastNameRequired"
            }) || "الاسم الأخير مطلوب";
        }

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        // Clear errors and submit
        setFormErrors({});
        const identifierType = identifier.includes('@') ? 'email' : 'phone';
        const profileData = {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            additionalData: {
                [identifierType]: identifier.trim(),
                auth_provider: identifierType
            }
        };

        onProfileComplete(profileData);
    };

    const handleFirstNameChange = (e) => {
        setFirstName(e.target.value);
        if (formErrors.firstName) {
            setFormErrors(prev => ({ ...prev, firstName: null }));
        }
    };

    const handleLastNameChange = (e) => {
        setLastName(e.target.value);
        if (formErrors.lastName) {
            setFormErrors(prev => ({ ...prev, lastName: null }));
        }
    };

    return (
        <AuthWrapper
            key={ProfileCompletion.name}
            autoRedirect={false}
            redirectTo={null}
        >
            <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Avatar
                    sx={{
                        m: '0 auto',
                        mb: 2,
                        width: 56,
                        height: 56,
                        background: primaryGradient,
                        boxShadow: `0 8px 32px ${theme.palette.primary.main}30`
                    }}
                >
                    <PersonAdd sx={{ fontSize: 28 }} />
                </Avatar>
                <Typography
                    component="h2"
                    variant="h5"
                    sx={{
                        fontWeight: 600,
                        background: primaryGradient,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 1
                    }}
                >
                    {localizationProvider.translateByType({
                        type: "Internal",
                        text: "completeYourProfile"
                    }) || "أكمل ملفك الشخصي"}
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: '0.95rem' }}
                >
                    {localizationProvider.translateByType({
                        type: "Internal",
                        text: "profileCompletionDescription"
                    }) || "يرجى إدخال اسمك الأول والأخير لإكمال التسجيل"}
                </Typography>
            </Box>

            {error && (
                <Alert
                    severity="error"
                    sx={{
                        mb: 3,
                        borderRadius: 2,
                        '& .MuiAlert-icon': {
                            color: theme.palette.error.main
                        }
                    }}
                >
                    {error}
                </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                    {/* Identifier Display Field */}
                    <Grid item xs={12}>
                        <TextField
                            dir="ltr"
                            fullWidth
                            disabled
                            label={identifier.includes('@') ? (
                                localizationProvider.translateByType({
                                    type: "Internal",
                                    text: "email"
                                }) || "البريد الإلكتروني"
                            ) : (
                                localizationProvider.translateByType({
                                    type: "Internal",
                                    text: "phoneNumber"
                                }) || "رقم الهاتف"
                            )}
                            value={identifier.includes('@') ? identifier : `${countryCode}${identifier}`}
                            sx={{
                                mb: 1,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.action.hover,
                                    '& fieldset': {
                                        borderColor: theme.palette.divider,
                                    },
                                },
                                '& .MuiInputLabel-root.Mui-disabled': {
                                    color: theme.palette.text.secondary,
                                },
                                '& .MuiOutlinedInput-input.Mui-disabled': {
                                    WebkitTextFillColor: theme.palette.text.primary,
                                    color: theme.palette.text.primary,
                                    fontWeight: 500,
                                }
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            required
                            label={localizationProvider.translateByType({
                                type: "Internal",
                                text: "firstName"
                            }) || "الاسم الأول"}
                            value={firstName}
                            onChange={handleFirstNameChange}
                            error={!!formErrors.firstName}
                            helperText={formErrors.firstName}
                            disabled={loading}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-1px)',
                                        boxShadow: `0 4px 12px ${theme.palette.primary.main}20`
                                    },
                                    '&.Mui-focused': {
                                        transform: 'translateY(-1px)',
                                        boxShadow: `0 4px 12px ${theme.palette.primary.main}30`
                                    }
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            required
                            label={localizationProvider.translateByType({
                                type: "Internal",
                                text: "lastName"
                            }) || "الاسم الأخير"}
                            value={lastName}
                            onChange={handleLastNameChange}
                            error={!!formErrors.lastName}
                            helperText={formErrors.lastName}
                            disabled={loading}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-1px)',
                                        boxShadow: `0 4px 12px ${theme.palette.primary.main}20`
                                    },
                                    '&.Mui-focused': {
                                        transform: 'translateY(-1px)',
                                        boxShadow: `0 4px 12px ${theme.palette.primary.main}30`
                                    }
                                }
                            }}
                        />
                    </Grid>
                </Grid>

                <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={onBackToIdentifier}
                        disabled={loading}
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            px: 3,
                            py: 1.5,
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.main,
                            '&:hover': {
                                borderColor: theme.palette.primary.dark,
                                backgroundColor: `${theme.palette.primary.main}08`
                            }
                        }}
                    >
                        {localizationProvider.translateByType({
                            type: "Internal",
                            text: "back"
                        }) || "رجوع"}
                    </Button>

                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        sx={{
                            flex: 1,
                            borderRadius: 2,
                            textTransform: 'none',
                            py: 1.5,
                            background: primaryGradient,
                            boxShadow: `0 4px 16px ${theme.palette.primary.main}40`,
                            '&:hover': {
                                background: primaryGradient,
                                transform: 'translateY(-1px)',
                                boxShadow: `0 6px 20px ${theme.palette.primary.main}50`
                            },
                            '&:disabled': {
                                background: theme.palette.action.disabledBackground,
                                color: theme.palette.action.disabled
                            }
                        }}
                    >
                        {loading ? (
                            localizationProvider.translateByType({
                                type: "Internal",
                                text: "creatingProfile"
                            }) || "جاري إنشاء الملف الشخصي..."
                        ) : (
                            localizationProvider.translateByType({
                                type: "Internal",
                                text: "completeProfile"
                            }) || "إكمال الملف الشخصي"
                        )}
                    </Button>
                </Box>
            </Box>
        </AuthWrapper>
    );
};

export default ProfileCompletion;